import { useEffect, useMemo, useState } from "react";
import { AudioLines, Search, Heart, Play, Layers } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Pill, Spinner, cx } from "@/components/shared";

const CAT_TONE = { Nature: "#22c55e", Animals: "#FFB300", Environment: "#5B4DFF" };

export default function SoundLibrary() {
  const [lib, setLib] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [favorites, setFavorites] = useState([]);
  const [mix, setMix] = useState([]);

  useEffect(() => { api.get("/catalogs").then(({ data }) => setLib(data.sound_library)).catch(() => {}); }, []);

  const items = useMemo(() => {
    if (!lib) return [];
    let out = [];
    Object.entries(lib).forEach(([c, sounds]) => sounds.forEach((s) => out.push({ category: c, name: s })));
    if (cat !== "All") out = out.filter((i) => i.category === cat);
    if (q) out = out.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));
    return out;
  }, [lib, q, cat]);

  if (!lib) return <Spinner label="Loading Sound Library…" />;
  const cats = ["All", ...Object.keys(lib)];

  const toggleFav = (name) => setFavorites((f) => f.includes(name) ? f.filter((x) => x !== name) : [...f, name]);
  const toggleMix = (name) => {
    setMix((m) => m.includes(name) ? m.filter((x) => x !== name) : [...m, name]);
  };

  return (
    <CinematicStage>
      <StageHeader stage="12" room="The Sound Stage · SFX & Music" icon={AudioLines} title="Layer the perfect atmosphere"
        subtitle="Nature, animals and environments — search, preview, favorite and layer multiple sounds."
        actions={mix.length > 0 && <Pill tone="gold"><Layers className="w-3 h-3" /> {mix.length} in mix</Pill>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sounds…" data-testid="sound-search-input" className="bg-transparent outline-none text-sm w-full text-white placeholder:text-slate-500" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`sound-cat-${c}`}
              className={cx("px-4 py-2 rounded-xl text-sm border transition", cat === c ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:text-white")}>{c}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((i) => {
          const inMix = mix.includes(i.name);
          return (
            <GlassCard key={i.category + i.name} hover className={cx("p-4", inMix && "border-[#FFB300]/50")} data-testid="sound-card">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${CAT_TONE[i.category]}22` }}>
                  <AudioLines className="w-5 h-5" style={{ color: CAT_TONE[i.category] }} />
                </div>
                <Heart onClick={() => toggleFav(i.name)} className={cx("w-4 h-4 cursor-pointer", favorites.includes(i.name) ? "fill-[#FFB300] text-[#FFB300]" : "text-slate-500")} />
              </div>
              <div className="text-white text-sm font-medium">{i.name}</div>
              <div className="text-xs text-slate-500 mb-3">{i.category}</div>
              <div className="flex gap-2">
                <button onClick={() => toast.info(`Preview: ${i.name} (sound provider required)`)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white"><Play className="w-3 h-3" /> Play</button>
                <button onClick={() => toggleMix(i.name)} data-testid={`sound-mix-${i.name}`} className={cx("px-3 py-2 rounded-lg text-xs border", inMix ? "bg-[#FFB300] text-[#081229] border-[#FFB300]" : "bg-white/5 border-white/10 text-slate-300")}>{inMix ? "✓" : "+"}</button>
              </div>
            </GlassCard>
          );
        })}
      </div>
    </CinematicStage>
  );
}
